import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Spinner,
  Modal,
  Form
} from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import InformacionUsuario from "../modales/InformacionUsuario";
import AvatarCentrado from "../modales/AvatarCentrado";

import {API_BASE} from "../../services/apirest";
import { invalidateSolicitudesCache } from "../../services/solicitudesCache";

export default function AprobacionBaja() {
  const rolId = parseInt(localStorage.getItem('rol_id'), 10);
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  }), [token]);

  const [loading, setLoading] = useState(true);
  const [solicitudes, setSolicitudes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showFolioModal, setShowFolioModal] = useState(false);
  const [folioBaja, setFolioBaja] = useState('');
  const [executeLoading, setExecuteLoading] = useState(false);
  const triggerGlobalRefresh = useCallback(() => {
    invalidateSolicitudesCache(key => key.includes('/solicitudes.php'));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('solicitudes:refresh'));
      window.dispatchEvent(new Event('inbox:refresh'));
    }
  }, []);

  // determina qué etapa carga según rol
  const stageForRole = useMemo(() => {
    if (rolId === 3 || rolId === 2) return 'SolicitudBaja'; // supervisor autoriza
    if (rolId === 1 || rolId === 14) return 'PROCESS'; // quien captura baja (ve todo)
    return null;
  }, [rolId]);

  const stageLabels = {
  SolicitudBaja:     "Solicitud en baja",
  CapturaBaja:       "Captura de Baja",
  Ejecutada:         "Ejecutada",
  VistoDistribuidor: "Visto"
}
  const roleLabels = useMemo(() => ({
    8: "VENDEDOR (ACTIVE DIRECTORI/SIBEL TELCOM)",
    9: "VENDEDOR / INSTALADOR (TOA)",
    10: "SUPERVISOR",
    11: "ADMINISTRATIVO",
  }), []);

  const getRolNombre = (id) =>
      (roleLabels?.[Number(id)] ?? `Rol ${id}`).toUpperCase();
  const fetchList = useCallback(async () => {
    if (![1,2,3,14].includes(rolId)) return;
    if (!stageForRole) return;
    setLoading(true);
    try {
      // intento por baja_stage; si el backend no lo soporta deberías tener fallback
      const url = `${API_BASE}/solicitudes.php?baja_stage=${encodeURIComponent(stageForRole)}`;
      const res = await fetch(url, { headers });
      const json = await res.json();
      let list = json.result?.solicitudes || [];

      // para supervisor (rol 3) podrías filtrar sus supervisados si aplica
      if ((rolId === 3 || rolId === 2) && list.length) {
        // suponiendo que el profile viene con supervisor_email cargado si lo necesitas
        // (aquí se omite por simplicidad, puedes agregar filtro igual que en otros componentes)
      }

      setSolicitudes(list);
    } catch (err) {
      console.error(err);
      setSolicitudes([]);
    } finally {
      setLoading(false);
    }
  }, [headers, stageForRole, rolId]);

  useEffect(() => {
    if (![1,2,3,14].includes(rolId)) return;
    fetchList();
  }, [fetchList, rolId]);

  if (![1,2,3,14].includes(rolId)) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
        <Container className="d-flex justify-content-center" style={{ height:'60vh' }}>
          <Spinner animation="border" />
        </Container>
    );
  }

  const formatDate = (fecha) => {
    if (!fecha) return '-';
    const d = new Date(fecha);
    if (isNaN(d)) return fecha;
    return d.toLocaleDateString();
  };

  const handleOpen = (sol) => {
    setSelected(sol);
    setFolioBaja('');
    setShowModal(true);
  };
  const handleClose = () => {
    setSelected(null);
    setShowModal(false);
  };

  const handleApproveBaja = async () => {
    if (!selected) return;
    try {
      const payload = {
        action: 'aprobar_baja',
        SolicitudId: selected.SolicitudId
      };
      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (json.status === 'success' || json.status === 'ok') {
        Swal.fire('¡LISTO!', 'Se avanzó a Captura de baja.', 'success');
        // remover de lista actual
        setSolicitudes(prev => prev.filter(s => s.SolicitudId !== selected.SolicitudId));
        handleClose();
        triggerGlobalRefresh();
      } else {
        throw new Error(json.result?.error_msg || 'Error');
      }
    } catch (err) {
      Swal.fire('Error', err.message || 'No se pudo autorizar baja', 'error');
    }
  };

  const handleReturnToActivo = async () => {
    if (!selected) return;
    try {
      const payload = {
        SolicitudId: selected.SolicitudId,
        status: 'Activo'
      };
      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (json.status === 'success' || json.status === 'ok') {
        Swal.fire('¡LISTO!', 'Se devolvió a activo.', 'success');
        setSolicitudes(prev => prev.filter(s => s.SolicitudId !== selected.SolicitudId));
        handleClose();
        triggerGlobalRefresh();
      } else {
        throw new Error(json.result?.error_msg || 'Error');
      }
    } catch (err) {
      Swal.fire('Error', err.message || 'No se pudo devolver', 'error');
    }
  };

  const handleExecuteBaja = async (folio) => {
  if (!selected) return;
  setExecuteLoading(true);        // ⬅️ empieza el loading
  try {
    // 1) ejecutar baja (subflujo)
    const ejec = await fetch(`${API_BASE}/solicitudes.php`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        action: 'ejecutar_baja',
        SolicitudId: selected.SolicitudId
      })
    });
    const ejecJson = await ejec.json();
    if (!(ejecJson.status === 'success' || ejecJson.status === 'ok')) {
      throw new Error(ejecJson.result?.error_msg || 'No se pudo ejecutar baja');
    }

    // 2) actualizar con folio y poner inactivo y fecha_baja
    const now = new Date();
    const fecha_baja = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
    const upd = await fetch(`${API_BASE}/solicitudes.php`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        SolicitudId: selected.SolicitudId,
        status: 'Inactivo',
        folio_baja: folio,
        fecha_baja
      })
    });
    const updJson = await upd.json();
    if (!(updJson.status === 'success' || updJson.status === 'ok')) {
      throw new Error(updJson.result?.error_msg || 'No se pudo finalizar baja');
    }

    Swal.fire('Listo', 'Baja ejecutada e inactivada con folio.', 'success');
    setSolicitudes(prev => prev.filter(s => s.SolicitudId !== selected.SolicitudId));
    setShowFolioModal(false);
    setShowModal(false);
    triggerGlobalRefresh();
  } catch (err) {
    Swal.fire('Error', err.message || 'Fallo al procesar baja', 'error');
  } finally {
    setExecuteLoading(false);     // ⬅️ termina el loading
  }
};


  return (
      <div className="app-container">
        <Container className="my-5">
          <Card className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">APROBACIONES DE BAJA</h5>
            </Card.Header>
            <Card.Body>
              {solicitudes.length === 0 ? (
                  <p className="text-center text-muted">No hay bajas en esta etapa.</p>
              ) : (
                  <Table hover responsive>
                    <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>RFC</th>
                      <th>Fecha</th>
                      <th>Etapa baja</th>
                      <th className="text-center">Acción</th>
                    </tr>
                    </thead>
                    <tbody>
                    {solicitudes.map(s => (
                        <tr key={s.SolicitudId}>
                          <td>{`${s.nombre_usuario || ''} ${s.apellido_paterno || ''}`.trim()}</td>
                          <td>{s.rfc}</td>
                          <td>{formatDate(s.Fecha)}</td>
                          <td>{stageLabels[s.baja_stage] || '-'}</td>
                          <td className="text-center">
                            {((rolId === 3 || rolId === 2 || rolId === 1 || rolId === 14)) && s.baja_stage === 'SolicitudBaja' && (
                                <Button size="sm" variant="primary" onClick={() => handleOpen(s)}>
                                  Autorizar baja
                                </Button>
                            )}
                            {(rolId === 1 || rolId === 14 || rolId === 2) && s.baja_stage === 'CapturaBaja' && (
                                <Button size="sm" variant="danger" onClick={() => handleOpen(s)}>
                                  Procesar
                                </Button>
                            )}
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </Table>
              )}
            </Card.Body>
          </Card>
        </Container>

        {/* Modal principal */}
        <Modal show={showModal} onHide={handleClose} centered size="lg" backdrop="static" style={{ zIndex: 11000 }}>
          <Modal.Header closeButton>
            <Modal.Title>
              {(rolId === 3 || rolId === 2)
                  ? `Autorizar baja `
                  : `Procesar baja `}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selected && (
                <>
                  <AvatarCentrado src={selected?.foto_url} alt="Foto del vendedor"/>
                  <InformacionUsuario
                      user={selected}
                      title="Distribuidor"
                      cols={2}
                      fields={[
                        { key: "distribuidor_nombre", label: "Distribuidor" },
                        { key: "distribuidor_rs",     label: "Razón Social" },
                      ]}
                  />

                  <InformacionUsuario
                      user={selected}
                      title="Información del Usuario"
                      cols={3}
                      fields={[
                        { key: "nombre_completo", label: "Nombre", render: u => [u?.nombre_usuario,u?.apellido_paterno,u?.apellido_materno].filter(Boolean).join(" ") },
                        { key: "rfc",             label: "RFC",    render: u => (u?.rfc||"—").toUpperCase() },
                        { key: "numero_vendedor",label: "Número Empleado" },
                        { key: "ccven",    label: "CVVEN" },
                        { key: "region_hub",    label:"Hub" },
                        { key: "usuario_red",      label: "Usuario red",      render: (u) => (u?.usuario_red || "—").toUpperCase() },
                        { key: "Fecha",          label: "Fecha Ingreso", render: (u) => u?.Fecha ? new Date(u.Fecha).toLocaleDateString("es-MX") : "—" },
                      ]}
                  />
                  <InformacionUsuario
                      user={selected}
                      title="Puesto"
                      cols={1}
                      fields={[
                        {
                          key: "rol_id",
                          label: "Puesto",
                          render: (u) => u?.rol_id ? getRolNombre(u.rol_id) : "—",
                        },
                      ]}
                  />
                  <InformacionUsuario
                      user={selected}
                      headerStyle={{ backgroundColor: '#dc3545', color: '#fff' }}
                      title="Motivo de baja"
                      cols={2}
                      fields={[
                        { key: "baja_stage", label: "Etapa",       render: u => stageLabels[u?.baja_stage] || u?.baja_stage || "—" },
                        { key: "razon_baja", label: "Razón de baja" },
                      ]}
                  />
                </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {(rolId === 3 || rolId === 2)&& selected?.baja_stage === 'SolicitudBaja' && (
                <>
                  <Button variant="secondary" onClick={handleReturnToActivo}>
                    Devolver a activo
                  </Button>
                  <Button variant="success" onClick={handleApproveBaja}>
                    Autorizar
                  </Button>
                </>
            )}
            {(rolId === 1 || rolId === 14 || rolId === 2) && selected?.baja_stage === 'CapturaBaja' && (
                <>
                  <Button variant="secondary" onClick={() => {
                    setShowModal(false);
                    setShowFolioModal(true)
                  }}>
                    Ejecutar baja
                  </Button>
                </>
            )}
            <Button variant="outline-secondary" onClick={handleClose}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de folio (para ejecución) */}
        <Modal
            show={showFolioModal}
            onHide={() => setShowFolioModal(false)}
            centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Introduce folio de baja</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Folio</Form.Label>
              <Form.Control
                  type="text"
                  value={folioBaja}
                  onChange={e => setFolioBaja(e.target.value)}
                  placeholder="p.ej. BAJA-2025-001"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              disabled={!folioBaja.trim() || executeLoading}
              onClick={() => {
                handleExecuteBaja(folioBaja.trim());
              }}
            >
              {executeLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Procesando...
                </>
              ) : (
                "Confirmar ejecución"
              )}
            </Button>

          </Modal.Footer>
        </Modal>
      </div>
  );
}
